/**
 * Build a mission that shares another mission's incoming and outgoing edges.
 *
 * Incoming: the new mission copies the source's dependsOn / unlockConditions,
 * so it can start under the same prerequisites.
 * Outgoing: wherever a downstream formula mentions the source, the new mission
 * is inserted beside that entry in the same AND/OR block, with the same
 * branch and wait.
 */
import { isWorkNode } from './noteUtils';
import {
  getAllConditionTaskIds,
  getDependencyTaskId,
  hasAnyConditions,
  hasConditionForTask,
  normalizeToV2,
  removeTaskFromConditions,
} from '../../shared/ConditionsDropdown/utils/conditionHelpers';

const PARALLEL_OFFSET_X = 48;

function cloneJson(value) {
  return JSON.parse(JSON.stringify(value));
}

export function isParallelMissionSource(task) {
  return isWorkNode(task) && task.nodeType !== 'decision';
}

function listsTask(dependsOn, taskId) {
  return (Array.isArray(dependsOn) ? dependsOn : []).some(
    (dep) => getDependencyTaskId(dep) === taskId,
  );
}

function usesConditions(task, tasks) {
  return hasAnyConditions(task?.unlockConditions, tasks);
}

function effectivelyDependsOn(task, sourceId, tasks) {
  if (usesConditions(task, tasks)) {
    return getAllConditionTaskIds(task.unlockConditions, tasks).includes(sourceId);
  }
  return listsTask(task.dependsOn, sourceId);
}

function sameConditionSlot(a, b) {
  return (a?.branch ?? null) === (b?.branch ?? null)
    && (a?.waitSeconds ?? null) === (b?.waitSeconds ?? null);
}

/** Insert a copy of each source entry into the same block, just after it. */
function mirrorBesideInConditions(unlockConditions, sourceId, newTaskId) {
  const v2 = cloneJson(normalizeToV2(unlockConditions));
  return {
    version: 2,
    blocks: (v2.blocks || []).map((block) => {
      const conditions = [];
      for (const condition of block.conditions || []) {
        conditions.push(condition);
        if (condition?.taskId !== sourceId) continue;
        const sibling = { ...condition, taskId: newTaskId };
        const duplicate = conditions.some((entry) => (
          entry.taskId === newTaskId && sameConditionSlot(entry, sibling)
        ));
        if (!duplicate) conditions.push(sibling);
      }
      return { ...block, conditions };
    }),
  };
}

function mirroredDependsOnEntry(dependsOn, sourceId, newTaskId) {
  if (!listsTask(dependsOn, sourceId) || listsTask(dependsOn, newTaskId)) return null;
  const sourceDep = dependsOn.find((dep) => getDependencyTaskId(dep) === sourceId);
  if (!sourceDep || typeof sourceDep === 'string') return newTaskId;
  return { ...cloneJson(sourceDep), taskId: newTaskId };
}

function withMirroredDependsOn(task, sourceId, newTaskId) {
  const mirrored = mirroredDependsOnEntry(task.dependsOn, sourceId, newTaskId);
  if (!mirrored) return task.dependsOn;
  return [...(task.dependsOn || []), mirrored];
}

function withParallelOutput(task, sourceId, newTaskId, tasks) {
  if (!isWorkNode(task) || task.id === sourceId || task.id === newTaskId) return task;
  if (!effectivelyDependsOn(task, sourceId, tasks)) return task;

  const dependsOn = withMirroredDependsOn(task, sourceId, newTaskId);
  const dependsChanged = dependsOn !== task.dependsOn;

  if (usesConditions(task, tasks)) {
    if (hasConditionForTask(task.unlockConditions, newTaskId, tasks)) {
      return dependsChanged ? { ...task, dependsOn } : task;
    }
    return {
      ...task,
      ...(dependsChanged ? { dependsOn } : {}),
      unlockConditions: mirrorBesideInConditions(task.unlockConditions, sourceId, newTaskId),
    };
  }

  if (!dependsChanged) return task;
  return { ...task, dependsOn };
}

export function copyIncomingDependencies(source, sourceId) {
  const dependsOn = (Array.isArray(source.dependsOn) ? cloneJson(source.dependsOn) : [])
    .filter((dep) => {
      const id = getDependencyTaskId(dep);
      return id && id !== sourceId;
    });

  if (!source.unlockConditions) {
    return { dependsOn };
  }

  let unlockConditions = cloneJson(source.unlockConditions);
  if (hasConditionForTask(unlockConditions, sourceId)) {
    unlockConditions = removeTaskFromConditions(unlockConditions, sourceId);
  }
  if (!hasAnyConditions(unlockConditions)) {
    return { dependsOn };
  }
  return { dependsOn, unlockConditions };
}

function placementFromSource(source) {
  const x = (typeof source.x === 'number' ? source.x : 100) + PARALLEL_OFFSET_X;
  const y = typeof source.y === 'number' ? source.y : 40;
  const displayPosition = source.position
    ? { x: source.position.x + PARALLEL_OFFSET_X, y: source.position.y }
    : { x, y };
  return { x, y, displayPosition };
}

/**
 * @returns {{ newTaskId: string, newTask: object, tasks: object[], displayPosition: {x: number, y: number} } | null}
 */
export function buildParallelMission(tasks, sourceId, { newTaskId, now }) {
  const source = (tasks || []).find((task) => task.id === sourceId);
  if (!isParallelMissionSource(source) || !newTaskId) return null;

  const { x, y, displayPosition } = placementFromSource(source);
  const inputs = copyIncomingDependencies(source, sourceId);

  const newTask = {
    id: newTaskId,
    name: '',
    order: (tasks?.length || 0) + 1,
    groupId: null,
    dependsOn: inputs.dependsOn,
    status: 'pending',
    x,
    y,
    createdAt: now,
    updatedAt: now,
    startedAt: null,
    completedAt: null,
    estimatedDurationMinutes: null,
    dueDate: null,
    ...(inputs.unlockConditions ? { unlockConditions: inputs.unlockConditions } : {}),
    ...(source.sourceCampaign ? { sourceCampaign: source.sourceCampaign } : {}),
    ...(source._campaignOffset != null ? { _campaignOffset: source._campaignOffset } : {}),
    ...(source.position
      ? {
          position: displayPosition,
          _originalPosition: { x, y },
        }
      : {}),
  };

  const withOutputs = (tasks || []).map((task) => withParallelOutput(task, sourceId, newTaskId, tasks));
  return {
    newTaskId,
    newTask,
    tasks: [...withOutputs, newTask],
    displayPosition,
  };
}
